import { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff } from 'lucide-react';
import type { WizardMode, CampaignTypeKey } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import BrandPicker from './BrandPicker';
import CampaignTypePicker from './CampaignTypePicker';
import CreativeStudio from './CreativeStudio';
import TargetingStep from './TargetingStep';
import ScheduleStep from './ScheduleStep';
import LivePreview from './LivePreview';
import PublishStep from './PublishStep';
import FestivalPicker from './FestivalPicker';
import { getCampaignTypeByKey } from '@/lib/campaignTypeRegistry';
import { creativeCopyIssues } from '@/lib/creativeCopy';
import { BrandLogoUploader } from '@/components/BrandLogoUploader';

// ─── Constants ──────────────────────────────────────────────────────────────

// Step labels are mode-aware. For festival mode the first step is the
// festival + tier picker (NOT a business picker) and the second step
// is the sponsoring-brand picker, since festivals are stacked
// monetization products with multiple separate sponsors per event.
const STEPS_BY_MODE: Record<WizardMode, { label: string; desc: string }[]> = {
  sponsor: [
    { label: 'Brand',     desc: 'Select advertiser' },
    { label: 'Template',  desc: 'Choose campaign type' },
    { label: 'Creative',  desc: 'Upload or generate assets' },
    { label: 'Targeting', desc: 'Corridors & time window' },
    { label: 'Schedule',  desc: 'Flight dates & contract' },
    { label: 'Review',    desc: 'Confirm & publish' },
  ],
  festival: [
    { label: 'Festival',  desc: 'Pick festival + sponsorship tier' },
    { label: 'Sponsor',   desc: 'Pick the sponsoring brand' },
    { label: 'Creative',  desc: 'Festival-themed copy & assets' },
    { label: 'Targeting', desc: 'Pre-filled from festival metadata' },
    { label: 'Schedule',  desc: 'Pre-filled from festival window' },
    { label: 'Review',    desc: 'Confirm contract & publish' },
  ],
  listing: [
    { label: 'Brand',     desc: 'Select advertiser' },
    { label: 'Template',  desc: 'Choose campaign type' },
    { label: 'Creative',  desc: 'Upload or generate assets' },
    { label: 'Targeting', desc: 'Corridors & time window' },
    { label: 'Schedule',  desc: 'Flight dates & contract' },
    { label: 'Review',    desc: 'Confirm & publish' },
  ],
};

const MODE_TITLES: Record<WizardMode, string> = {
  sponsor: 'New Sponsor Campaign',
  festival: 'New Festival Campaign',
  listing: 'New Map Listing',
};

// ─── Props ──────────────────────────────────────────────────────────────────

interface CampaignWizardProps {
  mode: WizardMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublished: () => void;
  /** Used by the Listings tab to lock the wizard to map-pin only. */
  lockedTemplateKey?: string;
  /** Used to lock the wizard's Step 1 to a specific campaign type card. */
  lockedTypeKey?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function CampaignWizard({
  mode, open, onOpenChange, onPublished, lockedTemplateKey, lockedTypeKey,
}: CampaignWizardProps) {
  const store = useWizardStore(mode);
  const {
    step, selectedBusiness, campaignTypes, headline, body, ctaText, newBusinessOpen,
    selectedFestival, festivalTier,
  } = store;

  // Use the mode-specific step labels and step count.
  const STEPS = STEPS_BY_MODE[mode];

  // User-toggleable preview pane visibility — gives form full width
  // when the operator wants to focus on dense card copy.
  const [previewVisible, setPreviewVisible] = useState(true);

  // Reset the store every time the dialog opens so stale data from a
  // previous session never pre-fills a new campaign.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      store.reset();
      // Re-apply mode-specific defaults after the reset
      if (mode === 'festival') {
        store.set({ pushAfterPublish: true, festivalMode: true });
      }
      if (lockedTemplateKey === 'shop_local_pin' || lockedTypeKey) {
        const typeKey = lockedTypeKey || 'sponsored_map_pin';
        const def = getCampaignTypeByKey(typeKey);
        if (def) {
          store.set({
            campaignType: def.key,
            campaignTypes: [def.key as CampaignTypeKey],
            templateKey: def.templateKey,
            templatePlacement: def.placement,
            templateLabel: def.label,
            estimatedDailyImpressions: def.estimatedDailyImpressions,
          });
        }
      }
      setPreviewVisible(true);
    }
    prevOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Guard: can the user advance past the current step?
  // The festival mode reorders the first two steps (Festival → Sponsor)
  // so the gate logic is mode-aware.
  const copyOk = creativeCopyIssues({ headline, body, ctaText }).length === 0;
  const canNext = (() => {
    if (mode === 'festival') {
      if (step === 0) return !!selectedFestival && !!festivalTier;
      if (step === 1) return !!selectedBusiness && !newBusinessOpen;
      if (step === 2) return copyOk;
      if (step === 3) return true;
      if (step === 4) return true;
      return false;
    }
    if (step === 0) return !!selectedBusiness && !newBusinessOpen;
    if (step === 1) return campaignTypes.length > 0;
    if (step === 2) return copyOk;
    if (step === 3) return true;
    if (step === 4) return true;
    return false;
  })();

  // The phone preview makes sense once we have either a business
  // (sponsor flow) or a festival + tier (festival flow). For festival
  // mode it appears earlier — from step 1 onward — so the operator
  // sees the festival theme tint kick in immediately.
  const canShowPreview =
    mode === 'festival'
      ? step >= 1 && !!selectedFestival
      : step >= 1 && !!selectedBusiness;
  const showPreview = canShowPreview && previewVisible;

  const progressPct = Math.round((step / (STEPS.length - 1)) * 100);

  const primaryTypeLabel = campaignTypes.length > 0
    ? getCampaignTypeByKey(campaignTypes[0])?.label || ''
    : '';
  const multiCount = campaignTypes.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1117] border-gray-800 text-white !max-w-[min(1280px,96vw)] w-[96vw] max-h-[94vh] overflow-hidden p-0 sm:rounded-2xl">
        <div className="flex flex-col h-[92vh]">

          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="shrink-0 border-b border-gray-800/80 bg-[#0d1117]">
            {/* Top bar: title + step labels */}
            <div className="flex items-center justify-between gap-4 px-5 pt-3 pb-2">
              <div className="min-w-0 flex-shrink">
                <DialogTitle className="text-white font-bold text-base tracking-tight truncate">
                  {MODE_TITLES[mode]}
                </DialogTitle>
                <DialogDescription className="text-gray-500 text-xs mt-0.5 truncate">
                  {/* For festival mode the contextual subtitle shows the
                      festival + tier first (so the operator always sees
                      which slot they're selling), then falls back to
                      brand once it's picked. */}
                  {mode === 'festival' && selectedFestival
                    ? `${selectedFestival.name}${
                        festivalTier ? ` · ${festivalTier.replace('_', ' ')} sponsor` : ''
                      }${selectedBusiness ? ` · ${selectedBusiness.name}` : ''}`
                    : selectedBusiness
                    ? `${selectedBusiness.name}${
                        primaryTypeLabel
                          ? ` · ${primaryTypeLabel}${multiCount > 1 ? ` (+${multiCount - 1} more)` : ''}`
                          : ''
                      }`
                    : mode === 'festival'
                    ? 'Pick a festival + sponsorship tier to start'
                    : 'Select a business to get started'}
                </DialogDescription>
              </div>

              {/* Step indicators — numbered pills */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {STEPS.map((s, i) => {
                  const done = i < step;
                  const active = i === step;
                  const locked = i > step;
                  return (
                    <button
                      key={i}
                      disabled={locked}
                      onClick={() => { if (!locked) store.setStep(i); }}
                      className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                        active
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                          : done
                            ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 cursor-pointer'
                            : 'bg-transparent text-gray-600 cursor-not-allowed'
                      }`}
                    >
                      {done
                        ? <Check className="w-3 h-3" />
                        : <span className="w-3.5 h-3.5 flex items-center justify-center rounded-full border border-current text-[9px] font-bold">{i + 1}</span>
                      }
                      <span className="hidden md:inline">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-0.5 bg-gray-800 mx-5 mb-3 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* ── Body ────────────────────────────────────────────────── */}
          <div className="flex-1 flex overflow-hidden relative">

            {/* Floating "Show phone preview" pill — appears on the right edge
                when the preview is collapsed, so the operator can always
                bring it back without hunting in the header. */}
            {canShowPreview && !previewVisible && (
              <button
                onClick={() => setPreviewVisible(true)}
                className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-20 items-center gap-1.5 px-3 py-2 rounded-l-xl bg-emerald-500/95 hover:bg-emerald-500 text-white text-xs font-semibold shadow-xl shadow-emerald-900/40 border border-emerald-300/30 transition-transform hover:-translate-x-0.5"
                title="Show phone preview"
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Phone preview</span>
              </button>
            )}


            {/* Left pane — form steps */}
            <div className={`${showPreview ? 'w-full md:w-[58%] xl:w-[60%]' : 'w-full'} overflow-y-auto p-5 md:p-6 lg:p-7 space-y-5 transition-all`}>

              {/* Business context pill — appears once a brand is picked.
                  In sponsor mode that happens on step 0, so the pill
                  shows from step 1 onward. In festival mode the brand is
                  picked on step 1, so the pill appears from step 2 onward. */}
              {selectedBusiness && step > (mode === 'festival' ? 1 : 0) && (
                <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-3 flex items-center gap-3">
                  {selectedBusiness.logo_url ? (
                    <img
                      src={selectedBusiness.logo_url}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border border-gray-600 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-emerald-400">
                        {selectedBusiness.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{selectedBusiness.name}</p>
                    {selectedBusiness.slogan ? (
                      <p className="text-xs text-gray-500 truncate">{selectedBusiness.slogan}</p>
                    ) : !selectedBusiness.logo_url ? (
                      <p className="text-[11px] text-amber-300/80 truncate">
                        No logo on file. Uploading one will replace the fallback letter everywhere this brand appears.
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Inline logo uploader — only when the brand is missing one.
                        Updates the wizard store + persists logo_url on the
                        local_business_listings row so future campaigns + the
                        in-app pin card pick it up automatically. */}
                    {!selectedBusiness.logo_url && (
                      <BrandLogoUploader
                        businessId={selectedBusiness.id}
                        businessName={selectedBusiness.name}
                        currentLogoUrl={selectedBusiness.logo_url}
                        onUploaded={(publicUrl) => {
                          store.set({
                            selectedBusiness: { ...selectedBusiness, logo_url: publicUrl },
                            gallerySelectedLogoUrl: publicUrl,
                          });
                        }}
                      />
                    )}
                    {canShowPreview && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`text-xs border hidden md:flex transition-colors ${
                          previewVisible
                            ? 'text-gray-400 hover:text-white border-gray-700 hover:border-gray-500'
                            : 'text-emerald-300 hover:text-emerald-200 border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20'
                        }`}
                        onClick={() => setPreviewVisible(!previewVisible)}
                        title={previewVisible ? 'Hide phone preview' : 'Show phone preview'}
                      >
                        {previewVisible ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                        {previewVisible ? 'Hide preview' : 'Show phone preview'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-500 hover:text-white text-xs border border-gray-700 hover:border-gray-500"
                      onClick={() => store.setStep(mode === 'festival' ? 1 : 0)}
                    >
                      Change {mode === 'festival' ? 'sponsor' : 'brand'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step content — festival mode swaps step 0 → festival picker
                  and step 1 → brand picker, since festivals are stacked
                  monetization products (one festival, many separate sponsor
                  cheques per tier). The CampaignTypePicker is skipped
                  because the tier choice on step 0 already determined
                  the campaign template. */}
              {step === 0 && (mode === 'festival' ? <FestivalPicker /> : <BrandPicker mode={mode} />)}
              {step === 1 && (mode === 'festival' ? <BrandPicker mode={mode} /> : <CampaignTypePicker mode={mode} lockedTypeKey={lockedTypeKey} />)}
              {step === 2 && <CreativeStudio mode={mode} />}
              {step === 3 && <TargetingStep mode={mode} />}
              {step === 4 && <ScheduleStep mode={mode} />}
              {step === 5 && (
                <PublishStep mode={mode} onPublished={onPublished} onClose={() => onOpenChange(false)} />
              )}
            </div>

            {/* Right pane — live device preview */}
            {showPreview && (
              <div
                className="hidden md:block w-[42%] xl:w-[40%] border-l border-gray-800 shrink-0 overflow-y-auto"
                style={{ background: 'radial-gradient(ellipse at 50% 20%, #0f1f2e 0%, #080d12 100%)' }}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                        Live Preview
                      </span>
                    </div>
                    <button
                      onClick={() => setPreviewVisible(false)}
                      className="text-gray-600 hover:text-gray-300 text-[11px] flex items-center gap-1"
                      title="Hide preview"
                    >
                      <EyeOff className="w-3 h-3" /> Hide
                    </button>
                  </div>
                  <LivePreview mode={mode} />
                </div>
              </div>
            )}
          </div>

          {/* ── Footer navigation ───────────────────────────────────── */}
          {step < 5 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800 bg-[#0d1117] shrink-0">
              <Button
                variant="ghost"
                className="text-gray-400 hover:text-white border border-transparent hover:border-gray-700"
                disabled={step === 0}
                onClick={() => store.prevStep()}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div className="flex items-center gap-3">
                {!canNext && step === 1 && (
                  <span className="text-[11px] text-amber-400/80 bg-amber-500/10 px-2 py-1 rounded-md">
                    Pick at least one template to continue
                  </span>
                )}
                {canNext && step === 1 && multiCount > 1 && (
                  <span className="text-[11px] text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-md">
                    {multiCount} templates → {multiCount} campaigns
                  </span>
                )}
                {!canNext && step === 2 && (
                  <span className="text-[11px] text-amber-400/80 bg-amber-500/10 px-2 py-1 rounded-md">
                    {headline.trim() ? 'Fix the copy warnings to continue' : 'Add a headline to continue'}
                  </span>
                )}
                <Button
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-5 shadow-lg shadow-emerald-900/40"
                  disabled={!canNext}
                  onClick={() => store.nextStep()}
                >
                  {step === 4 ? 'Review & Publish' : 'Next'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
